/**
 * Reading FORM cards, submitted answers and concept cards off the wire.
 *
 * Tolerant by design: these are persisted rows, possibly written by an older build. A
 * card we cannot fully read degrades to "no card" (a plain bubble), it never takes the
 * transcript down. Accepts the JSON string the API sends or an
 * already-parsed object.
 */
import type { SelectOption } from "../../wire";
import { asOption, asRecord, asString, isRecord, safeParse } from "../../wire";
import type {
  Concept,
  ConceptCardData,
  FormAnswerData,
  FormCard,
  FormFieldKind,
  FormFieldSpec,
  FormId,
  FormValue,
  FormValues,
  MoneyRange,
  OptionsStatus,
} from "./formTypes";
import { FORM_FIELD_KINDS, FORM_IDS, OPTIONS_STATUSES } from "./formTypes";

const asFormId = (raw: unknown): FormId | null =>
  FORM_IDS.find((id) => id === raw) ?? null;

const asFieldKind = (raw: unknown): FormFieldKind | null =>
  FORM_FIELD_KINDS.find((kind) => kind === raw) ?? null;

export const asOptionsStatus = (raw: unknown): OptionsStatus =>
  OPTIONS_STATUSES.find((status) => status === raw) ?? "ready";

/** A list of options (`[{value, label, ...}]`), tolerant of a JSON string. */
export const parseOptions = (raw: unknown): SelectOption[] => {
  const source = typeof raw === "string" ? safeParse(raw) : raw;
  return Array.isArray(source)
    ? source.map(asOption).filter((option) => option !== null)
    : [];
};

const asRange = (raw: unknown): MoneyRange | null => {
  if (!isRecord(raw)) return null;
  const when = asString(raw.when);
  if (!when) return null;
  return {
    when,
    min: asString(raw.min),
    max: asString(raw.max),
    recommended: asString(raw.recommended),
  };
};

const asField = (raw: unknown): FormFieldSpec | null => {
  if (!isRecord(raw)) return null;
  const key = asString(raw.key);
  const kind = asFieldKind(raw.kind);
  if (!key || !kind) return null;
  return {
    key,
    kind,
    label: asString(raw.label, key),
    hint: asString(raw.hint),
    required: raw.required === true,
    options: parseOptions(raw.options),
    optionsStatus: asOptionsStatus(raw.optionsStatus),
    minSelections:
      typeof raw.minSelections === "number" ? raw.minSelections : 0,
    maxSelections:
      typeof raw.maxSelections === "number" ? raw.maxSelections : null,
    suggested: asString(raw.suggested),
    minDate: asString(raw.minDate),
    maxDate: asString(raw.maxDate),
    currency: asString(raw.currency),
    ranges: Array.isArray(raw.ranges)
      ? raw.ranges.map(asRange).filter((range) => range !== null)
      : [],
    rangeBy: asString(raw.rangeBy),
    allowBarter: raw.allowBarter === true,
    brandId: asString(raw.brandId),
    width: raw.width === "half" ? "half" : "full",
  };
};

/** A FORM question, or null for anything that is not one (or cannot be drawn). */
export const parseFormCard = (raw: unknown): FormCard | null => {
  const source = asRecord(raw);
  if (!source || source.kind !== "FORM") return null;
  const formId = asFormId(source.formId);
  const action = asString(source.action);
  if (!formId || !action || !Array.isArray(source.fields)) return null;
  const fields = source.fields.map(asField).filter((field) => field !== null);
  // A form with no fields has nothing to submit; a plain bubble is more honest.
  if (!fields.length) return null;
  return {
    formId,
    action,
    prompt: asString(source.prompt),
    submitLabel: asString(source.submitLabel, "Enviar"),
    fields,
  };
};

const asFormValue = (raw: unknown): FormValue | null => {
  if (typeof raw === "string" || typeof raw === "boolean") return raw;
  if (Array.isArray(raw)) {
    return raw.every((item): item is string => typeof item === "string")
      ? raw
      : null;
  }
  if (!isRecord(raw)) return null;
  if (typeof raw.productId === "string") return { productId: raw.productId };
  if (typeof raw.url === "string") {
    return typeof raw.price === "string" && raw.price
      ? { url: raw.url, price: raw.price }
      : { url: raw.url };
  }
  if (typeof raw.amount === "string") return { amount: raw.amount };
  if (raw.barterOnly === true) return { barterOnly: true };
  return null;
};

/** A map of field key -> value; entries we cannot read are dropped. */
export const parseFormValues = (raw: unknown): FormValues => {
  const source = asRecord(raw);
  if (!source) return {};
  const values: FormValues = {};
  for (const [key, value] of Object.entries(source)) {
    const parsed = asFormValue(value);
    if (parsed !== null) values[key] = parsed;
  }
  return values;
};

/** The answer of a submitted form: `{kind: "FORM", formId, values}`. */
export const parseFormAnswer = (raw: unknown): FormAnswerData | null => {
  const source = asRecord(raw);
  if (!source || source.kind !== "FORM") return null;
  const formId = asFormId(source.formId);
  if (!formId) return null;
  return { formId, values: parseFormValues(source.values) };
};

/** One concept, as `CONCEPT_READY` and a concept card both carry it. */
export const parseConcept = (raw: unknown): Concept | null => {
  const source = asRecord(raw);
  if (!source) return null;
  const id = asString(source.id);
  const title = asString(source.title);
  if (!id || !title) return null;
  return {
    id,
    title,
    hook: asString(source.hook),
    description: asString(source.description),
    referenceHandle: asString(source.referenceHandle),
    format: asString(source.format),
  };
};

/** A concept card message: `{kind: "CONCEPT", concept, stale, chosen}`. */
export const parseConceptCard = (raw: unknown): ConceptCardData | null => {
  const source = asRecord(raw);
  if (!source || source.kind !== "CONCEPT") return null;
  const concept = parseConcept(source.concept);
  if (!concept) return null;
  return {
    concept,
    stale: source.stale === true,
    chosen: source.chosen === true,
  };
};
